import { Checkbox } from '@material-tailwind/react';
import Link from 'next/link';
import React, { FC } from 'react';
import { BiSad } from 'react-icons/bi';
import { FcCalendar, FcGoogle } from 'react-icons/fc';
import { SiMicrosoftoutlook } from 'react-icons/si';
import { ClipLoader } from 'react-spinners';
import { Provider } from '../../../utils/enums';
import { ExternalCalendar } from '../../../utils/types';
import { useUser } from '@auth0/nextjs-auth0';
import { UserInfo } from '../../../utils/interfaces';

type Props = {
  externalCalendars: ExternalCalendar[] | undefined;
  displayExtCalendars: number[];
  setDisplayExtCalendars: React.Dispatch<React.SetStateAction<number[]>>;
  redirectHref: string;
  loading: boolean;
};

const ExistingUserCalendars: FC<Props> = ({
  externalCalendars,
  displayExtCalendars,
  setDisplayExtCalendars,
  redirectHref,
  loading,
}) => {
  const { user } = useUser();
  const userInfo = user as UserInfo;
  const handleCheckBoxChange = (id: number) => {
    const calendarsClone = [...displayExtCalendars];
    const calendarIdx = displayExtCalendars.indexOf(id);
    if (calendarIdx === -1) {
      calendarsClone.push(id);
    } else {
      calendarsClone.splice(calendarIdx, 1);
    }
    setDisplayExtCalendars(calendarsClone);
  };

  const getCheckboxColor = (ec: ExternalCalendar) => {
    switch (ec.provider) {
      case Provider.FreeBusy: {
        return 'green';
      }
      case Provider.Google: {
        return 'amber';
      }
      case Provider.Microsoft: {
        return 'blue';
      }
      default: {
        return 'gray';
      }
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-stretch justify-between mt-0">
        <div className="flex flex-col">
          <h3 className="font-semibold pb-2 w-96 text-center">
            Select from {userInfo.userPreferences?.firstName}'s calendars…
          </h3>
          <div className="flex flex-col items-stretch pb-2 divide-y rounded-xl border-1 border-blue-gray-200 h-32 overflow-scroll bg-white text-black">
            {loading ? (
              <ClipLoader
                size={45}
                color={'#86E3A8'}
                className="self-center my-auto"
              />
            ) : (
              <>
                {externalCalendars?.length === 0 ? (
                  <p className="flex flex-row items-stretch gap-1">
                    No connected calendars
                    <BiSad />
                  </p>
                ) : (
                  externalCalendars?.map((ec: ExternalCalendar) => {
                    const provider = () => {
                      if (ec.provider === Provider.Google)
                        return <FcGoogle size={26} />;
                      else if (ec.provider === Provider.Microsoft)
                        return (
                          <SiMicrosoftoutlook
                            className="text-blue-600"
                            size={26}
                          />
                        );
                      else if (ec.provider === Provider.FreeBusy)
                        return <FcCalendar size={26} />;
                      else return <FcCalendar size={26} />;
                    };
                    return (
                      <div
                        key={ec.id}
                        className="flex flex-row items-center w-full gap-4 px-4 py-2"
                      >
                        <Checkbox
                          type="checkbox"
                          className="cursor-pointer"
                          color={getCheckboxColor(ec)}
                          ripple={true}
                          id={ec.id.toString()}
                          name={ec.name}
                          checked={!(displayExtCalendars.indexOf(ec.id) !== -1)}
                          onChange={() => handleCheckBoxChange(ec.id)}
                          value={ec.name}
                        />
                        <p className="text-md">{ec.name}</p>
                        {provider()}
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
          <p className="text-xs pt-2 -mb-2">
            Not your account?{' '}
            <Link
              href={`/api/auth/login?redirect-url=${redirectHref}&redirect-to-current=true`}
            >
              <a className="underline text-confirmed-primary">
                Sign in to yours
              </a>
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExistingUserCalendars;
